import type { ObjectId } from '@freightview/object-id'

import {
  vArray,
  vCountry,
  vInt,
  vMinValue,
  vNonEmptyString,
  vNumber,
  vObject,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
} from '../validation/index.ts'
import type { CountryUpper } from './country.ts'
import type { ShipType } from './ship-type.ts'

export type AddressOverrides = {
  company?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  contactPhoneExt?: string
  instructions?: string
  internalNotes?: string
  shipType?: ShipType
  dockHoursStart?: string
  dockHoursEnd?: string
}

export interface AccountLocation {
  _id: ObjectId
  formatted: string
  street: string
  city: string
  state: string
  stateCode: string
  country: CountryUpper
  postalCode: string
  lat: number
  lng: number
  timezone: string
}
export const accountLocationUpsertSchema = vObject({
  street: vNonEmptyString(),
  city: vNonEmptyString(),
  state: vNonEmptyString(),
  postalCode: vNonEmptyString(),
  country: vCountry,
})
export type AccountLocationUpsertModel = vOutput<
  typeof accountLocationUpsertSchema
>

export const locationImportTargetFields = [
  'street',
  'city',
  'state',
  'postalCode',
  'company',
  'contactName',
  'contactEmail',
  'contactPhone',
  'contactPhoneExt',
  'instructions',
  'internalNotes',
  'shipType',
  'dockHoursStart',
  'dockHoursEnd',
  'ignore',
] as const
export type LocationImportTargetField =
  (typeof locationImportTargetFields)[number]

export const inferLocationMappingsSchema = vObject({
  headers: vArray(vString()),
  sampleRows: vArray(vArray(vString())),
})
export type InferLocationMappingsModel = vOutput<
  typeof inferLocationMappingsSchema
>

export const locationColumnMappingSchema = vObject({
  column: vString(),
  field: vPicklist(locationImportTargetFields),
})
export const locationMappingsResultSchema = vObject({
  mappings: vArray(locationColumnMappingSchema),
})
export type LocationColumnMapping = vOutput<typeof locationColumnMappingSchema>
export type LocationMappingsResult = vOutput<
  typeof locationMappingsResultSchema
>

export const locationAddressCandidateSchema = vObject({
  index: vNumber(),
  street: vString(),
  city: vString(),
  state: vString(),
  postalCode: vString(),
})
export const validateLocationAddressesSchema = vObject({
  addresses: vArray(locationAddressCandidateSchema),
})
export type LocationAddressCandidate = vOutput<
  typeof locationAddressCandidateSchema
>
export type ValidateLocationAddressesModel = vOutput<
  typeof validateLocationAddressesSchema
>

export const locationAddressStatuses = [
  'valid',
  'invalid',
  'ambiguous',
  'placeholder',
] as const
export type LocationAddressStatus = (typeof locationAddressStatuses)[number]

export const locationAddressValidationSchema = vObject({
  index: vPipe(vNumber(), vInt(), vMinValue(0)),
  status: vPicklist(locationAddressStatuses),
  reason: vOptional(vString()),
  normalizedStreet: vOptional(vString()),
})
export const locationAddressValidationResultSchema = vObject({
  results: vArray(locationAddressValidationSchema),
})
export type LocationAddressValidation = vOutput<
  typeof locationAddressValidationSchema
>
export type LocationAddressValidationResult = vOutput<
  typeof locationAddressValidationResultSchema
>
